import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "applications-web-m365-google-workspace",
  silo: "reseau",
  h1: "Applications web (M365, Google Workspace) : 8 solutions quand tout bloque",
  metaTitle: "Applications web bloquées : solutions M365 et Google Workspace",
  metaDescription: "Outlook Web, Teams, Drive, Gmail ou Docs ne fonctionnent plus ? Diagnostiquez vite et corrigez les blocages les plus courants.",
  excerpt: "Connexion impossible, synchro bloquée, lenteurs, erreurs d'authentification : ce guide vous aide à dépanner rapidement vos applications web professionnelles.",
  publishedAt: "2026-04-21",
  readingMinutes: 7,
  relatedSlugs: ["probleme-outlook-gmail", "internet-lent-instable", "wifi-ne-fonctionne-plus", "logiciel-plante-ne-souvre-pas"],
  faq: [
    {
      question: "Pourquoi Teams ou Gmail fonctionnent sur mobile mais pas sur mon PC ?",
      answer: "Le problème vient souvent du navigateur (cache, extensions, profil corrompu) ou d'une politique réseau locale. Testez en navigation privée puis dans un autre navigateur pour isoler la cause.",
    },
    {
      question: "Dois-je réinstaller Office si Outlook Web ou OneDrive ne se synchronisent plus ?",
      answer: "Pas forcément. La réinstallation est rarement la première étape. Commencez par vérifier la connexion, l'authentification, l'espace disque local et les comptes connectés avant toute action lourde.",
    },
    {
      question: "Comment savoir si la panne vient de Microsoft/Google ou de mon entreprise ?",
      answer: "Consultez les pages de statut des services et testez depuis un autre réseau (partage de connexion mobile). Si le problème disparaît, la cause est probablement locale (DNS, proxy, filtrage, pare-feu).",
    },
  ],
  content: [
    {
      type: "p",
      text: "Les blocages sur Microsoft 365 et Google Workspace sont souvent multifactoriels : navigateur, session utilisateur, réseau, DNS, sécurité, ou quotas atteints. L'objectif est d'aller vite avec un diagnostic structuré pour éviter les arrêts de production.",
    },
    {
      type: "h2",
      text: "1) Vérifier l'état des services cloud avant toute manipulation",
    },
    {
      type: "p",
      text: "Avant de modifier votre poste, vérifiez si la panne est globale (incident fournisseur) ou locale. Un incident côté éditeur peut provoquer des connexions intermittentes, des délais d'ouverture ou des erreurs 5xx.",
    },
    {
      type: "h2",
      text: "2) Tester en navigation privée puis sur un autre navigateur",
    },
    {
      type: "p",
      text: "Un cache corrompu, un cookie invalide ou une extension peut bloquer la connexion. Ouvrez l'application en navigation privée. Si cela fonctionne, videz le cache, supprimez les cookies du domaine concerné et désactivez les extensions suspectes.",
    },
    {
      type: "h2",
      text: "3) Contrôler l'authentification et la session (MFA, compte multiple)",
    },
    {
      type: "p",
      text: "Les erreurs de redirection infinie ou de connexion refusée viennent souvent d'un conflit de comptes (perso/pro) et d'un jeton expiré. Déconnectez tous les comptes, reconnectez uniquement le compte professionnel, puis validez la MFA.",
    },
    {
      type: "h2",
      text: "4) Examiner la qualité réseau (latence, pertes, WiFi)",
    },
    {
      type: "p",
      text: "Teams, Meet, Drive ou OneDrive supportent mal les microcoupures. Une connexion [internet lente ou instable](slug:internet-lent-instable) provoque des déconnexions, des synchronisations partielles et des erreurs aléatoires.",
    },
    {
      type: "h2",
      text: "5) Vérifier DNS, proxy et filtrage de sécurité",
    },
    {
      type: "p",
      text: "Un DNS mal configuré, un proxy d'entreprise ou une suite de sécurité trop stricte peut bloquer certains endpoints cloud. Testez temporairement un DNS public, et vérifiez les règles de filtrage SSL/HTTPS sur votre réseau.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "Attention aux blocages silencieux",
      text: "Certaines solutions de sécurité n'affichent pas d'alerte visible pour l'utilisateur. L'application semble 'tourner dans le vide' alors qu'une requête critique est bloquée en arrière-plan.",
    },
    {
      type: "h2",
      text: "6) Contrôler la synchronisation locale (OneDrive, Drive, Outlook)",
    },
    {
      type: "p",
      text: "Sur Windows, une synchro peut échouer si le disque est saturé, si le chemin local contient des caractères non supportés, ou si la base de données locale est corrompue. Vérifiez aussi l'espace libre et les conflits de fichiers.",
    },
    {
      type: "h2",
      text: "7) Mettre à jour le poste et corriger les erreurs système",
    },
    {
      type: "p",
      text: "Des composants Windows obsolètes, un navigateur trop ancien ou des dépendances cassées peuvent bloquer les applications web modernes. Si des symptômes similaires touchent d'autres logiciels, consultez aussi notre guide sur les [logiciels qui plantent](slug:logiciel-plante-ne-souvre-pas).",
    },
    {
      type: "h2",
      text: "8) Standardiser un plan de reprise rapide",
    },
    {
      type: "p",
      text: "Documentez une procédure interne : test webmail, test autre navigateur, test autre réseau, vérification identité, contrôle DNS/proxy. Cette checklist réduit fortement le temps de résolution lors des prochaines pannes.",
    },
    {
      type: "callout",
      variant: "info",
      title: "Quand escalader au support technique",
      text: "Si le blocage persiste au-delà de 30 à 45 minutes malgré ces vérifications, un diagnostic assisté est recommandé pour éviter la perte de données et limiter l'impact sur l'équipe.",
    },
  ],
};

export default article;
